import { CircleCheck, Flag, FlagOff, Send, Undo2, type LucideIcon } from "lucide-react";
import type { ManualState } from "@arc/contracts";

/** The review transitions available FROM a given state (ADR-018). `key` maps to a mutation hook; `mark` needs a comment
 *  dialog first. Mirrors the legacy edit-mode stepper (Submit → Confirm; Return to draft; Unmark). `marked` leaves ONLY
 *  via `unmark` (no marked→draft — closes the un-publish loophole). Pure — shared by the kebab menu + the drawer stepper. */
export type ManualTransitionKey = "submit" | "confirm" | "mark" | "unmark" | "return";

export interface ManualTransition {
  key: ManualTransitionKey;
  /** A catalogue KEY, not a label: this module is pure and cannot call a hook, so a baked string would be English
   *  for every caller. The exact union keeps `t()` type-checked at the call site (ADR-048 Phase 4). */
  labelKey:
    | "facilityManagement.transition.submit"
    | "facilityManagement.transition.confirm"
    | "facilityManagement.transition.return"
    | "facilityManagement.transition.mark"
    | "facilityManagement.transition.unmark";
  /** The lucide glyph both the kebab item + the drawer button render (one source of truth for the two surfaces). */
  icon: LucideIcon;
  /** Opens the mark-comment dialog before firing (only `mark`). */
  needsComment?: boolean;
  /** Rendered with the destructive accent (the "un-publish"/flag side). */
  destructive?: boolean;
  /** This transition requires the facility **`confirm`** tier server-side (publish / unpublish / clear-flag), so it is
   *  hidden from a `manuals_manage`-only caller (else the button would deterministically 403 — panel finding). Mirrors
   *  the service: `confirm`, `unmark`, and `return-to-draft` *from `confirmed`* need `confirm`; `submit`/`mark`/withdraw
   *  need only `manuals_manage`. Note `return` carries DIFFERENT tiers per source state, so the flag lives per-entry. */
  needsConfirm?: boolean;
}

/** ALL transitions valid from a state (ADR-018), each tagged with its required tier — the raw machine. Callers filter by
 *  the caller's capability via {@link availableManualTransitions}; the pure unit tests assert this raw shape. */
export function manualTransitions(state: ManualState): ManualTransition[] {
  switch (state) {
    case "draft":
      return [{ key: "submit", labelKey: "facilityManagement.transition.submit", icon: Send }];
    case "ready_for_review":
      return [
        { key: "confirm", labelKey: "facilityManagement.transition.confirm", icon: CircleCheck, needsConfirm: true },
        { key: "return", labelKey: "facilityManagement.transition.return", icon: Undo2 }, // withdraw own submission = manuals_manage
      ];
    case "confirmed":
      return [
        { key: "mark", labelKey: "facilityManagement.transition.mark", icon: Flag, needsComment: true, destructive: true },
        { key: "return", labelKey: "facilityManagement.transition.return", icon: Undo2, needsConfirm: true }, // reopen a PUBLISHED manual = confirm
      ];
    case "marked":
      return [{ key: "unmark", labelKey: "facilityManagement.transition.unmark", icon: FlagOff, needsConfirm: true }];
    default:
      return [];
  }
}

/** The transitions a caller may ACTUALLY perform, filtered by their best-effort facility tier (the server re-checks). A
 *  `manuals_manage` floor is required for any transition; the `confirm`-tier steps additionally need `canConfirm`. Used by
 *  both the kebab + the drawer so a `manuals_manage`-only caller never sees a guaranteed-403 Confirm/Unmark/reopen button. */
export function availableManualTransitions(
  state: ManualState,
  caps: { canManage: boolean; canConfirm: boolean },
): ManualTransition[] {
  if (!caps.canManage) return [];
  return manualTransitions(state).filter((t) => !t.needsConfirm || caps.canConfirm);
}
